import { Component, ElementRef, input, InputSignal, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AttachmentModalData } from '../../../Attachment/attachment.modal.model';
import { Attachment } from '../../../Attachment/attachment.model';
import { AttachmentService } from '../../../Attachment/attachment.service';
import { EntityAttachmentsComponent } from '../../../Attachment/Entity/entity-attachments.component';
import { EntityTypeIds } from '../../../constants.new';
import { AccordionComponent, AccordionPanelComponent } from 'ngx-bootstrap/accordion';
import { FileDropDirective } from '../../../UI-Lib/Directives';
import { PanelChevronComponent } from '../../../UI-Lib/Weissman-Panel/panel.chevron.component';
import { AceLoaderComponent } from '../../../UI-Lib/ACE/Loader/loader.component';
import { AppealRecommendationTemplateService, ArccAttachmentItem } from '../appealRecommendationTemplate.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

export interface ArccAttachmentSite {
    siteId: number;
    siteName: string;
    isOpen: boolean;
    hasAttachments: boolean;
    attachmentsLoading: boolean;
    attachments: ArccAttachmentItem[];
    modalData: AttachmentModalData;
    uploadFile?: File;
}

export interface ArccSiteAttachmentFiles {
    site: ArccAttachmentSite;
    files: File[];
}

@Component({
    selector: 'arcc-attachments',
    templateUrl: './arccAttachments.component.html',
    styleUrls: ['./arccAttachments.component.scss'],
    imports: [
        AccordionComponent,
        AccordionPanelComponent,
        EntityAttachmentsComponent,
        FileDropDirective,
        PanelChevronComponent,
        AceLoaderComponent,
    ],
})
export class ArccAttachmentsComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _attachmentService: AttachmentService,
        private readonly _arccTemplateService: AppealRecommendationTemplateService,
    ) {}

    @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

    sites: InputSignal<Core.AppealRecommendationSiteInfoModel[]> = input<Core.AppealRecommendationSiteInfoModel[]>([]);
    taxYear: InputSignal<number> = input<number>();
    editMode: InputSignal<boolean> = input<boolean>(false);

    availableSites: ArccAttachmentSite[] = [];
    loading = false;

    /** Tracks which site the hidden file-input is targeting. */
    private _uploadTargetSite: ArccAttachmentSite | null = null;
    private _destroy$: Subject<void> = new Subject();

    ngOnInit(): void {
        this._arccTemplateService.selectedAttachments$.pipe(takeUntil(this._destroy$)).subscribe((x) => {
            this.availableSites.forEach((site) => {
                site.attachments.forEach((attachment) => {
                    attachment.selected = x.some((a) => a.attachmentId === attachment.attachmentID);
                });
            });
        });

        this._buildSiteModels();
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    async loadSiteAttachments(site: ArccAttachmentSite): Promise<void> {
        if (site.attachmentsLoading) {
            return;
        }

        site.attachmentsLoading = true;

        try {
            const attachments = await this._attachmentService.getAll(EntityTypeIds.SITE, site.siteId, false, site.siteId);

            if (!site.attachments.length) {
                site.attachments = attachments.map((a) => ({
                    ...a,
                    selected: false,
                }));
            } else {
                const current = site.attachments.map((x) => x.attachmentID);
                attachments
                    .filter((x) => !current.includes(x.attachmentID))
                    .forEach((x) =>
                        site.attachments.push({
                            ...x,
                            selected: false,
                        }),
                    );
            }
        } finally {
            site.attachmentsLoading = false;
        }
    }

    toggleAttachmentSelection(site: ArccAttachmentSite, attachment: ArccAttachmentItem): void {
        attachment.selected = !attachment.selected;

        if (attachment.selected) {
            this._arccTemplateService.addAttachments([attachment]);
        } else {
            this._arccTemplateService.removeSelectedAttachment(attachment.attachmentID);
        }
    }

    uploadFile(site: ArccAttachmentSite): void {
        this._uploadTargetSite = site;
        this.fileInput.nativeElement.value = '';
        this.fileInput.nativeElement.click();
    }

    onFileInputChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        if (!target.files?.length) {
            return;
        }

        const files = Array.from(target.files);
        if (this._uploadTargetSite) {
            this._uploadTargetSite.uploadFile = files[0];
            this._uploadTargetSite = null;
        } else {
            this._arccTemplateService.addFiles(files);
        }
    }

    onFilesDropped(site: ArccAttachmentSite, files: File[]): void {
        site.uploadFile = files[0];
    }

    onSiteAttachmentsChanged(site: ArccAttachmentSite, attachments: Attachment[]): void {
        // Remove previously selected attachments from this site that are no longer present
        site.attachments
            .filter((x) => x.selected)
            .map((x) => x.attachmentID)
            .forEach((x) => this._arccTemplateService.removeSelectedAttachment(x));

        site.attachments = attachments.map((a) => ({
            ...a,
            selected: false,
        }));
        site.hasAttachments = attachments.length > 0;
    }

    getSelectedAttachments(): ArccAttachmentItem[] {
        return this.availableSites.flatMap((s) => s.attachments.filter((a) => a.selected));
    }

    private _buildSiteModels(): void {
        const sites = this.sites() || [];
        this.availableSites = sites.map((info) => ({
            siteId: info.siteId,
            siteName: info.siteName,
            isOpen: false,
            hasAttachments: info.hasAttachments,
            attachmentsLoading: false,
            attachments: [],
            modalData: {
                entityType: 'Site',
                parentId: null,
                parentType: null,
                entityName: info.siteName,
                entityDescription: null,
                belowParcelEntity: null,
                entityData: {
                    typeId: EntityTypeIds.SITE,
                    id: info.siteId,
                    name: info.siteName,
                },
                year: this.taxYear(),
                disableYears: false,
                singleYear: false,
                readOnly: false,
                startOnPhotos: false,
            } as AttachmentModalData,
        }));
    }
}
